/**
 * Phase 27.38.3 — Knowledge highlight carousel for the agent dashboard.
 *
 * Shows the latest 3-4 active resources with 16:9 covers so agents
 * see fresh training / compliance / sales content the moment they
 * log in. Click on any card opens the resource (YouTube/Drive) in a
 * new tab. A "Ver todos" link routes to the full /knowledge grid.
 *
 * Widget is silently empty (renders null) when there's no content
 * yet — dashboards should degrade gracefully.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Play, FileText as FileTextIcon, ExternalLink, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { knowledgeAPI } from '@/lib/api';

const MAX_CARDS = 3;

function CoverThumb({ resource }) {
  const [errored, setErrored] = useState(false);
  const url = resource?.cover_url;
  if (!url || errored) {
    const IconEl = resource?.resource_type === 'video' ? Play : FileTextIcon;
    return (
      <div className="aspect-video w-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 rounded-t-lg">
        <IconEl className="w-8 h-8 text-slate-400" />
      </div>
    );
  }
  return (
    <div className="aspect-video w-full relative overflow-hidden bg-slate-100 rounded-t-lg">
      <img
        src={url}
        alt={resource?.title || 'Cover'}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={() => setErrored(true)}
      />
      {resource?.resource_type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
          <div className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center shadow-md opacity-90 group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 text-slate-900 ml-0.5" fill="currentColor" />
          </div>
        </div>
      )}
    </div>
  );
}

export function KnowledgeHighlightsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await knowledgeAPI.list({ active_only: true });
        if (cancelled) return;
        setItems((r.data?.items || []).slice(0, MAX_CARDS));
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openLink = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Render nothing while loading or empty — dashboards should keep
  // their density instead of showing a loading skeleton for an
  // optional widget.
  if (loading || items.length === 0) return null;

  return (
    <Card className="animate-slideIn" data-testid="knowledge-highlights-widget">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <BookOpen size={18} className="text-violet-600" />
          Knowledge — New resources
        </CardTitle>
        <Link
          to="/knowledge"
          className="text-xs text-sky-700 hover:text-sky-900 flex items-center gap-1 font-medium"
          data-testid="knowledge-highlights-view-all"
        >
          Ver todos <ArrowRight size={12} />
        </Link>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <button
              type="button"
              key={r.resource_id}
              onClick={() => openLink(r.url)}
              className="group text-left rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all overflow-hidden bg-white"
              data-testid={`knowledge-highlight-${r.resource_id}`}
            >
              <CoverThumb resource={r} />
              <div className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-sm text-slate-900 line-clamp-2 leading-snug">
                    {r.title}
                  </h4>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                </div>
                {r.description && (
                  <p className="text-xs text-slate-500 line-clamp-2">
                    {r.description}
                  </p>
                )}
                {(r.categories || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {r.categories.slice(0, 2).map((c) => (
                      <Badge key={c} variant="outline" className="text-[9px] py-0 px-1.5 bg-slate-50 text-slate-600 border-slate-200">
                        {c}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default KnowledgeHighlightsWidget;
